export interface FuzzySearchableBreed {
    value: string;
    label: string;
    otherNames?: string[];
}

interface ScoredResult extends FuzzySearchableBreed {
    score: number;
    matchedField: "name" | "otherName";
    matchedValue: string;
}

// Type definitions for configuration structure
interface GeneralConfig {
    EXACT_MATCH: number;
    STARTS_WITH: number;
    MAIN_NAME_BONUS: number;
    MIN_SCORE_THRESHOLD: number;
}

interface SequenceConfig {
    WEIGHT: number;
    MIN_SCORE_FOR_WORD_MATCH: number;
    BASE_MULTIPLIER: number;
}

interface WordConfig {
    STARTS_WITH_BASE: number;
    STARTS_WITH_LENGTH_BONUS: number;
    REVERSE_STARTS_WITH: number;
    CONTAINS: number;
    MIN_LENGTH_FOR_CONTAINS: number;
}

interface SubstringConfig {
    AT_START: number;
    NOT_AT_START: number;
    MIN_LENGTH_RATIO: number;
}

interface ScoringConfig {
    GENERAL: GeneralConfig;
    SEQUENCE: SequenceConfig;
    WORD: WordConfig;
    SUBSTRING: SubstringConfig;
}

// Nested configuration for better organization
const SCORING: ScoringConfig = {
    GENERAL: {
        EXACT_MATCH: 100,
        STARTS_WITH: 95,
        MAIN_NAME_BONUS: 5,
        MIN_SCORE_THRESHOLD: 25
    },
    SEQUENCE: {
        WEIGHT: 0.7,
        MIN_SCORE_FOR_WORD_MATCH: 50,
        BASE_MULTIPLIER: 80
    },
    WORD: {
        STARTS_WITH_BASE: 70,
        STARTS_WITH_LENGTH_BONUS: 20,
        REVERSE_STARTS_WITH: 60,
        CONTAINS: 40,
        MIN_LENGTH_FOR_CONTAINS: 3
    },
    SUBSTRING: {
        AT_START: 90,
        NOT_AT_START: 75,
        MIN_LENGTH_RATIO: 0.3
    }
} as const;

const DEFAULT_MAX_RESULTS = 8;
const MIN_SEARCH_LENGTH = 1;

// Utility for consistent string normalization
const normalize = (str: string): string => str.toLowerCase().trim();

/**
 * Calculate match score between search query and target string
 * @param search - User's search query
 * @param target - String to match against (breed name or alias)
 * @param isMainName - Whether this is the primary breed name (gets bonus points)
 * @returns Score from 0-100+, where 100 is exact match, 95 is prefix match
 */
function calculateScore(search: string, target: string, isMainName = false): number {
    const searchLower = normalize(search);
    const targetLower = normalize(target);

    if (!searchLower || !targetLower) return 0;

    // High-priority checks (return immediately)
    if (searchLower === targetLower) {
        return SCORING.GENERAL.EXACT_MATCH;
    }

    if (targetLower.startsWith(searchLower)) {
        return SCORING.GENERAL.STARTS_WITH;
    }

    // If no high-priority match, run fuzzy heuristics
    const bestFuzzyScore = Math.max(
        calculateWordMatch(searchLower, targetLower),
        calculateSequenceMatch(searchLower, targetLower),
        calculateSubstringMatch(searchLower, targetLower)
    );

    // Apply threshold
    if (bestFuzzyScore < SCORING.GENERAL.MIN_SCORE_THRESHOLD) {
        return 0;
    }

    // Apply main name bonus if applicable
    return isMainName ? bestFuzzyScore + SCORING.GENERAL.MAIN_NAME_BONUS : bestFuzzyScore;
}

/**
 * Calculate how well characters appear in sequence
 */
function calculateSequenceMatch(search: string, target: string): number {
    let matchedChars = 0;
    let targetIndex = 0;

    for (const searchChar of search) {
        const foundIndex = target.indexOf(searchChar, targetIndex);
        if (foundIndex !== -1) {
            matchedChars++;
            targetIndex = foundIndex + 1;
        }
    }

    const charRatio = matchedChars / search.length;
    const lengthPenalty = Math.min(search.length / target.length, 1);

    return charRatio * lengthPenalty * SCORING.SEQUENCE.BASE_MULTIPLIER;
}

/**
 * Calculate word-based matching score
 */
function calculateWordMatch(search: string, target: string): number {
    const searchWords = search.split(/\s+/).filter(Boolean);
    const targetWords = target.split(/\s+/).filter(Boolean);

    if (searchWords.length === 0) return 0;

    const wordScores = searchWords.map(searchWord => Math.max(...targetWords.map(targetWord => calculateSingleWordScore(searchWord, targetWord)), 0));

    const matchedWords = wordScores.filter(score => score > 0).length;
    if (matchedWords === 0) return 0;

    const averageScore = wordScores.reduce((sum, score) => sum + score, 0) / searchWords.length;
    const completionBonus = matchedWords / searchWords.length;

    return averageScore * completionBonus;
}

/**
 * Calculate score for matching a single word
 */
function calculateSingleWordScore(searchWord: string, targetWord: string): number {
    if (searchWord === targetWord) {
        return SCORING.GENERAL.EXACT_MATCH;
    }

    if (targetWord.startsWith(searchWord)) {
        const lengthRatio = searchWord.length / targetWord.length;
        return SCORING.WORD.STARTS_WITH_BASE + lengthRatio * SCORING.WORD.STARTS_WITH_LENGTH_BONUS;
    }

    if (searchWord.startsWith(targetWord)) {
        return SCORING.WORD.REVERSE_STARTS_WITH;
    }

    if (targetWord.includes(searchWord) && searchWord.length >= SCORING.WORD.MIN_LENGTH_FOR_CONTAINS) {
        return SCORING.WORD.CONTAINS;
    }

    const seqScore = calculateSequenceMatch(searchWord, targetWord);
    return seqScore > SCORING.SEQUENCE.MIN_SCORE_FOR_WORD_MATCH ? seqScore * SCORING.SEQUENCE.WEIGHT : 0;
}

/**
 * Calculate substring matching with position-based scoring
 */
function calculateSubstringMatch(search: string, target: string): number {
    const position = target.indexOf(search);
    if (position === -1) return 0;

    const positionScore = position === 0 ? SCORING.SUBSTRING.AT_START : SCORING.SUBSTRING.NOT_AT_START;

    const lengthRatio = search.length / target.length;
    return positionScore * Math.max(lengthRatio, SCORING.SUBSTRING.MIN_LENGTH_RATIO);
}

/**
 * Score all possible matches for a breed
 */
function scoreBreed(breed: FuzzySearchableBreed, search: string): ScoredResult[] {
    const results: ScoredResult[] = [];

    // Score main name
    const nameScore = calculateScore(search, breed.label, true);
    if (nameScore > 0) {
        results.push({
            ...breed,
            score: nameScore,
            matchedField: "name",
            matchedValue: breed.label
        });
    }

    // Score alternative names
    if (breed.otherNames?.length) {
        for (const otherName of breed.otherNames) {
            const otherScore = calculateScore(search, otherName, false);
            if (otherScore > 0) {
                results.push({
                    ...breed,
                    score: otherScore,
                    matchedField: "otherName",
                    matchedValue: otherName
                });
            }
        }
    }

    return results;
}

/**
 * Search for breeds using fuzzy matching
 * @param search - User's search query (min 3 characters)
 * @param options - Array of breeds to search through
 * @param maxResults - Maximum number of results to return (default: 8)
 * @returns Filtered and sorted array of matching breeds
 */
export function searchBreeds(search: string, options: FuzzySearchableBreed[], maxResults = DEFAULT_MAX_RESULTS): FuzzySearchableBreed[] {
    const trimmedSearch = search.trim();

    // Return empty for short searches
    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
        return [];
    }

    // Score all breeds
    const allResults = options.flatMap(breed => scoreBreed(breed, trimmedSearch));

    // Keep only the best score for each breed
    const bestScoresByBreed = new Map<string, ScoredResult>();
    for (const result of allResults) {
        const existing = bestScoresByBreed.get(result.value);
        if (!existing || result.score > existing.score) {
            bestScoresByBreed.set(result.value, result);
        }
    }

    // Sort by score and return top results
    return Array.from(bestScoresByBreed.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, maxResults)
        .map(({ score, matchedField, matchedValue, ...breed }) => breed);
}
